import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, useNavigate } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { Building2, Copy, Trash2 } from 'lucide-react';

import { Button } from '@/components/new-ui/button';
import { TableCell, TableRow } from '@/components/new-ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/new-ui/tooltip';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { projectAtom } from '@/features/navigation/atoms/projectAtom';
import { getDate } from '@/utils/getDate';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import { getFinalAreaRoom } from '@/utils/getFinalAreaRoom';
import { getFinalLivingArea } from '@/utils/getFinalLivingArea';
import { getFinalPrice } from '@/utils/getFinalPrice';

import { saveEstatesListScroll } from '../hooks/use-estates-scroll-restore';
import { EstateChips } from './estate-chips';
import type { EstateListData } from './estate-list';
import { EstateProjectLinkButton } from './estate-project-link-button';

export const isEstateRow = (estate: EstateListData) => 'estateDetails' in estate;

export const getEstateLink = (estate: EstateListData) =>
  isEstateRow(estate)
    ? ({ to: '/estates/$estateId', params: { estateId: estate.id, projectId: '' } } as const)
    : ({ to: '/projects/$projectId', params: { projectId: estate.id, estateId: '' } } as const);

type RowActionsProps = {
  estateId: string;
  /** Project the estate is linked to, the link button links or unlinks it */
  project?: EstateListData['project'];
  onCopy: (id: string) => void;
  onDelete: (id: string) => void;
  className?: string;
};

/** Project link, copy and delete icon buttons, never trigger the surrounding link or row. */
export const EstateRowActions = ({
  estateId,
  project,
  onCopy,
  onDelete,
  className,
}: RowActionsProps) => {
  const { t } = useTranslation();

  const handle = (event: MouseEvent, action: (id: string) => void) => {
    event.preventDefault();
    event.stopPropagation();
    action(estateId);
  };

  return (
    <div className={className ?? 'flex items-center justify-end gap-1'}>
      <EstateProjectLinkButton estateId={estateId} project={project} />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-foreground"
        title={t('copy')}
        aria-label={t('copy')}
        onClick={(event) => handle(event, onCopy)}
      >
        <Copy className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive"
        title={t('delete')}
        aria-label={t('delete')}
        onClick={(event) => handle(event, onDelete)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

type EstatesTableRowProps = {
  estate: EstateListData;
  onCopy: (id: string) => void;
  onDelete: (id: string) => void;
};

export const EstatesTableRow = ({ estate, onCopy, onDelete }: EstatesTableRowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setProject = useSetAtom(projectAtom);

  const isEstate = isEstateRow(estate);
  const link = getEstateLink(estate);
  const creatorName = [estate.creator?.firstName, estate.creator?.lastName]
    .filter(Boolean)
    .join(' ');
  const livingArea = getFinalLivingArea(estate);

  const handleOpen = () => {
    saveEstatesListScroll(estate.id);
    // Same project source as the row actions and the grid, an estate row reports it as project
    setProject(
      estate.project
        ? { id: estate.project.id, name: estate.project.name ?? '' }
        : (estate.projects ?? null)
    );
  };

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button, a')) return;
    handleOpen();
    navigate(link);
  };

  return (
    <TableRow data-estate-id={estate.id} className="cursor-pointer" onClick={handleRowClick}>
      <TableCell>
        <Link {...link} onClick={handleOpen} className="flex items-center gap-3">
          <div className="relative shrink-0">
            <EstateImage
              className="h-12 w-12 overflow-hidden rounded-md [&>div:first-child]:bg-muted [&>div:first-child]:text-muted-foreground"
              thumbnailSize="thumbnail200"
              iconSize={20}
              {...(estate.thumbnail?.mediaId ? { thumbnailId: estate.thumbnail.mediaId } : {})}
            />
            {!isEstate ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="absolute -bottom-1 -right-1 rounded-full bg-primary p-0.5">
                    <Building2 className="h-3 w-3 text-nu-primary-foreground" aria-hidden />
                    <span className="sr-only">{t('project')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">{t('project')}</TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <EstateChips estate={estate} compact />
            <div
              className="line-clamp-2 break-words font-medium text-foreground"
              title={estate.name ?? undefined}
            >
              {getRenderValue(estate.name)}
            </div>
            {estate.address?.address ? (
              <div className="line-clamp-1 break-words text-sm text-muted-foreground">
                {estate.address.address}
              </div>
            ) : null}
          </div>
        </Link>
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums text-foreground">
        {getRenderValue(getFinalPrice(estate), 1)}
      </TableCell>
      <TableCell className="hidden whitespace-nowrap tabular-nums text-foreground lg:table-cell">
        {livingArea || livingArea === 0 ? `${livingArea} m²` : getRenderValue(livingArea, 1)}
      </TableCell>
      <TableCell className="hidden tabular-nums text-foreground lg:table-cell">
        {getRenderValue(getFinalAreaRoom(estate), 1)}
      </TableCell>
      <TableCell className="hidden text-sm text-foreground xl:table-cell">
        {getRenderValue(creatorName, 1)}
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums text-foreground">
        {getDate(estate.createdAt)}
      </TableCell>
      <TableCell>
        {isEstate ? (
          <EstateRowActions
            estateId={estate.id}
            project={estate.project ?? estate.projects}
            onCopy={onCopy}
            onDelete={onDelete}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
};
